import { useEffect, useState } from 'react';
import type { FC } from 'react';
import {
  VStack,
  useToast,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { PageHeading, Panel } from '@bemap/ui';
import {
  KanbanBoard,
  LeadsListView,
  useFunnels,
  useCrmLeads,
  useFunnelContext,
} from '@bemap/crm-microfrontends';
import type { SalesFunnel, LeadWithFunnel } from '@bemap/crm-microfrontends';
import {
  getFunnels,
  getCrmLeads,
  moveLeadToStep,
} from '@/services/crm-service';
import { useNavigate } from 'react-router-dom';

const CrmPage: FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const [view, setView] = useState<'kanban' | 'list'>('kanban');
  const { selectedFunnel, setFunnels } = useFunnelContext();

  const fetchFunnels = async () => {
    const res = await getFunnels();
    return (res.message || []) as SalesFunnel[];
  };

  const { funnels, loading: funnelsLoading } = useFunnels(fetchFunnels);

  useEffect(() => {
    if (funnels.length > 0) {
      setFunnels(funnels);
    }
  }, [funnels, setFunnels]);

  const fetchLeads = async (options?: { funnelId?: number; stepId?: string; view?: string }) => {
    const res = await getCrmLeads(options?.funnelId, options?.stepId, options?.view);
    return (res.message || []) as LeadWithFunnel[];
  };

  const { leads, loading: leadsLoading, refetch: refetchLeads } = useCrmLeads(
    fetchLeads,
    selectedFunnel ? { funnelId: selectedFunnel.id, view } : undefined
  );

  const handleLeadMove = async (leadId: number, fromStepId: string, toStepId: string) => {
    if (!selectedFunnel) return;

    if (fromStepId === toStepId) return;

    if (Number(toStepId)) {
      navigate(`/crm/leads/${leadId}`);
      return;
    }

    try {
      await moveLeadToStep(leadId, {
        funnel_id: selectedFunnel.id,
        step_id: toStepId,
      });
      // Refetch leads to get updated step information
      await refetchLeads();
      toast({ status: 'success', title: t('general.updated') || 'Updated' });
    } catch {
      toast({ status: 'error', title: t('general.error') || 'Error' });
      // Refetch on error to revert UI state
      await refetchLeads();
    }
  };

  const handleLeadClick = (lead: LeadWithFunnel) => {
    navigate(`/leads/${lead.id}`);
  };

  if (funnelsLoading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <VStack w="100%" gap={4} align="stretch">
      <PageHeading
        title={t('crm.title') || 'CRM'}
        actions={[
          {
            label: view === 'kanban' ? 'List View' : 'Kanban View',
            onClick: () => setView(view === 'kanban' ? 'list' : 'kanban'),
          },
        ]}
      />
      <Panel>
        <VStack align="stretch" gap={4}>
          {selectedFunnel ? (
            view === 'kanban' ? (
              <KanbanBoard
                funnel={selectedFunnel}
                leads={leads}
                loading={leadsLoading}
                onLeadClick={handleLeadClick}
                onLeadMove={handleLeadMove}
              />
            ) : (
              <LeadsListView
                leads={leads}
                loading={leadsLoading}
                onLeadClick={handleLeadClick}
                funnel={selectedFunnel}
              />
            )
          ) : (
            <Center h="200px">
              {t('crm.no_funnel') || 'No funnel selected. Please create a funnel first.'}
            </Center>
          )}
        </VStack>
      </Panel>
    </VStack>
  );
};

export default CrmPage;

